import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { useTranslation } from 'react-i18next';

import useNavigation from '../hooks/useNavigation';
import useSearch from '../hooks/useSearch';
import useWithGA from '../hooks/useWithGA';
import useTransformGatsbyData from '../hooks/useTransformGatsbyData';
import useStrapiPageData from '../hooks/data/useStrapiPageData';

import MetatagsForLayout from '../components/MetatagsForLayout';

import CONSTANTS_COMPANY_SIZE, {
  sizes as CONSTANTS_SIZE,
} from '../constants/companySize';

import {
  getArrayItemsToStringNice,
  createUrlPath,
  isCustomerOrAffiliate,
  isCustomer,
} from '../utils/utils';

import {
  getLogoAssetObject,
  getPitchAssets,
  categoryLinksCms,
  unEscapeHtml,
} from '../utils/sharedFunc';

import {
  groupReviewQuestionsBySpecialNames,
  getAverageReviewsRatings,
} from '../utils/reviews';

import { PAGE_COMPARE_SYSTEM_STATIC } from '../constants/strapi';
import { Compare } from '../ui/templates/compare/Compare';

import { Graph } from '../../static/icons/graph';
import { Hands } from '../../static/icons/hands';
import { Tasks } from '../../static/icons/tasks';
import { Touch } from '../../static/icons/touch';

export const pageQuery = graphql`
  query ComparisonTemplate(
    $commonCategories: [String!]
    $systemIds: [String!]
    $pageBuildId: String
  ) {
    comparedCategories: allBwCategory(
      filter: { id: { in: $commonCategories } }
    ) {
      nodes {
        data
      }
    }
    comparedSystems: allBwSystem(filter: { id: { in: $systemIds } }) {
      nodes {
        data
      }
    }
    comparedSystemProps: allBwSystemProp(
      filter: { category: { in: $commonCategories } }
    ) {
      nodes {
        category
        dbName
        filter
      }
    }
    comparedSystemReviews: allBwReview(
      filter: { systemId: { in: $systemIds } }
    ) {
      nodes {
        data
      }
    }

    bwPageBuildData(id: { eq: $pageBuildId }) {
      data
    }

    bwStrapiPageSystem: bwSinglePage(id: { eq: "page-system" }) {
      data
    }
  }
`;

function ComparsionTemplate({ pageContext, location, data }) {
  const { trackOutboundLink, savePageView, handleDataLayer } = useWithGA();
  const { locale, systemIds } = pageContext;
  const { t } = useTranslation();
  const bwPageBuildData = useTransformGatsbyData(data.bwPageBuildData);
  const siteUrls = bwPageBuildData.pageData.siteUrls || {};

  const { menu, footer } = useNavigation(siteUrls, location);
  const search = useSearch();

  const comparedCategories = useTransformGatsbyData(data.comparedCategories);
  const comparedSystems = useTransformGatsbyData(data.comparedSystems);
  const comparedSystemProps = useTransformGatsbyData(data.comparedSystemProps);
  const comparedSystemReviews = useTransformGatsbyData(
    data.comparedSystemReviews
  );
  const bwStrapiPageSystem = useTransformGatsbyData(data.bwStrapiPageSystem);

  const productFuncFields = comparedSystemProps.reduce((acc, curr) => {
    const { dbName, category } = curr;
    const updatedObj = { ...acc };
    if (acc[category]) {
      updatedObj[category].push(dbName);
    } else {
      updatedObj[category] = [];
      updatedObj[category].push(dbName);
    }
    return updatedObj;
  }, {});

  const generalFunctions = [];

  // Handle reviews data start
  const systemsReviewsMap = comparedSystemReviews.reduce((acc, review) => {
    const { systemV2Id } = review;
    if (!acc[systemV2Id]) {
      acc[systemV2Id] = {
        ALL: [],
        LIKE_MOST: [],
        LIKE_LEAST: [],
      };
    }

    const grouppedQuestions = groupReviewQuestionsBySpecialNames(
      review.questions,
      ['LIKE_MOST', 'LIKE_LEAST']
    );

    acc[systemV2Id].ALL.push(review);

    // Do not push LIKE_MOST and LIKE_LEAST if review score is below 3.5
    const reviewAverageQuestionsRating = getAverageReviewsRatings(
      review,
      locale
    );

    const averageRating =
      reviewAverageQuestionsRating.reduce(
        (sum, question) => sum + question.averageRating,
        0
      ) / reviewAverageQuestionsRating.length;

    if (averageRating > 3.5) {
      acc[systemV2Id].LIKE_MOST.push(...grouppedQuestions.LIKE_MOST);
      acc[systemV2Id].LIKE_LEAST.push(...grouppedQuestions.LIKE_LEAST);
    }

    return acc;
  }, {});

  const commonReviewsRatingQuestionTexts = Object.values(
    systemsReviewsMap
  ).reduce((acc, reviewsData) => {
    const averageSystemRatings = getAverageReviewsRatings(
      reviewsData.ALL,
      locale
    );

    averageSystemRatings.forEach((question) => {
      const num = acc[question.text] || 0;
      acc[question.text] = num + 1;
    });

    return acc;
  }, {});

  const validQuestionsTextsToCompare = Object.entries(
    commonReviewsRatingQuestionTexts
  ).reduce((acc, [text, count]) => {
    if (count >= 2) {
      acc.push(text);
    }
    return acc;
  }, []);
  // -- Handle reviews data end

  Object.keys(productFuncFields).forEach((category) => {
    const categoryData = comparedCategories.find(
      (cat) => cat.categoryDbName === category
    );
    const { categoryName } = categoryData;
    const items = productFuncFields[category]
      .map((func) => ({
        key: func,
        title: t([`prodFunc.${category}.${func}`, `prodFunc.common.${func}`]), // ACCOUNTING.automaticReports, // func,
        description: null,
      }))
      .filter((item) => !!item.title)
      .sort((a, b) => a.title.localeCompare(b.title));
    generalFunctions.push({
      key: category,
      title: t('compare.productFunc', { category: categoryName }), //  t('compare.productFunc', { category: categoryName }),
      items,
    });
  });

  const generalPrices = {
    title: t('compare.priceInfo'),
    items: [
      {
        key: 'freeTrial',
        title: t('compare.freeTrial'),
      },
      {
        key: 'price',
        title: t('compare.priceFrom'),
      },
      {
        key: 'priceAdditional',
        title: t('compare.additionalPrices'),
      },
      {
        key: 'MODULE',
        title: t('compare.priceModule'),
        description: t('compare.priceModuleHover'),
      },
      {
        key: 'PACKAGE',
        title: t('compare.priceSubscription'),
        description: t('compare.priceSubscriptionHover'),
      },
      {
        key: 'LICENSE',
        title: t('compare.priceLicense'),
        description: t('compare.priceLicenseHover'),
      },
    ],
  };

  const generalCompanySizes = {
    title: t('compare.companySize'),
    subTitle: t('guideHome.formCompanySizeHelp'),
    items: ['MICRO', 'SMALL', 'MEDIUM', 'LARGE'].map((sizeName) => {
      const constant = CONSTANTS_COMPANY_SIZE[sizeName];
      const size = CONSTANTS_SIZE[sizeName];
      return {
        key: sizeName,
        value: size,
        title: t(constant, { size }),
      };
    }),
  };

  const generalPlatforms = {
    title: t('compare.platform'),
    items: [
      {
        value: 'SAAS',
        title: t('compare.cloud'),
        description: t('compare.cloudHover'),
      },
      {
        value: 'ONPREMISE',
        title: t('compare.onpremise'),
        description: t('compare.onpremiseHover'),
      },
      {
        value: 'mobileApp',
        title: t('compare.mobileApp'),
      },
    ],
  };

  useEffect(() => {
    savePageView({
      type: 'COMPARE_STATIC',
      ids: systemIds,
    });
  }, []);

  let hasPriceAdditional = false;
  let hasPrice = false;

  const systemsComponentData = comparedSystems.map((system) => {
    const {
      freeTrial,
      priceModel,
      systemSiteDataForLocale,
      deployment,
      mobileApps,
      id,
      defaultCategory,
    } = system;

    const {
      companyName: company,
      systemName: name,
      systemNameUrl,
      customerType,
      systemExternalUrl,
      extraBtnLink,
      whySystemDescription,
      whyCompanyDescription,
      marketPositionDescription,
      solvesClientDescription,
      priceAdditional,
      price,
      mediaAssets,
    } = systemSiteDataForLocale;

    const allSystemReviews = systemsReviewsMap[id]?.ALL || [];
    const averageRatings = getAverageReviewsRatings(allSystemReviews, locale)
      .filter((question) =>
        validQuestionsTextsToCompare.includes(question.text)
      )
      .map((question) => ({
        title: question.text,
        value: question.averageRating,
      }));

    const pros = systemsReviewsMap[id]?.LIKE_MOST.filter((question) => {
      const hasText = question.answerText?.[locale]?.length > 20;
      return hasText;
    })
      .slice(0, 2)
      .map((question) => question.answerText?.[locale]);

    const cons = systemsReviewsMap[id]?.LIKE_LEAST.filter((question) => {
      const hasText = question.answerText?.[locale]?.length > 20;
      return hasText;
    })
      .slice(0, 2)
      .map((question) => question.answerText?.[locale]);

    const logoAsset = getLogoAssetObject(mediaAssets);
    const logoUrl = logoAsset?.url
      ? `https://ik.imagekit.io/businesswith/tr:w-100,h-40,cm-pad_resize,dpr-2/logo/${logoAsset?.url}`
      : null;

    const pitchVideos = getPitchAssets(mediaAssets);

    const readAboutMoreHref = createUrlPath([
      bwStrapiPageSystem?.buildData?.slug,
      systemNameUrl,
    ]);

    const externalLink = systemExternalUrl || extraBtnLink;

    const readMoreHref =
      isCustomerOrAffiliate(customerType) && externalLink
        ? externalLink
        : readAboutMoreHref;
    const trackingFn =
      isCustomerOrAffiliate(customerType) && externalLink
        ? () => {
            trackOutboundLink({}, externalLink, null, 'compare-page', id, {
              page_type: 'Compare page',
              system_name: name,
              result_position: '',
            });
          }
        : null;

    const prices = generalPrices?.items.map((generalPriceModel) => {
      if (generalPriceModel.key === 'freeTrial') {
        if (freeTrial) {
          return {
            title: generalPriceModel.title,
            value: 'YES',
          };
        }
        return {
          title: generalPriceModel.title,
          value: 'NO',
        };
      }

      if (generalPriceModel.key === 'priceAdditional') {
        if (priceAdditional) {
          hasPriceAdditional = true;
          return {
            title: generalPriceModel.title,
            value: priceAdditional,
          };
        }
        return {
          title: generalPriceModel.title,
          value: '-',
        };
      }

      if (generalPriceModel.key === 'price') {
        if (price) {
          hasPrice = true;
          return {
            title: generalPriceModel.title,
            value: price,
          };
        }
        return {
          title: generalPriceModel.title,
          value: '-',
        };
      }

      const systemPriceModelTrueOrFalse = priceModel?.find(
        (model) => model === generalPriceModel.key
      );

      return {
        title: generalPriceModel.title,
        value: systemPriceModelTrueOrFalse ? 'YES' : 'NO',
      };
    });

    const platforms = generalPlatforms?.items
      ?.filter((generalPlatform) => {
        if (generalPlatform.value === 'mobileApp') {
          const mobileApp = !!mobileApps.length;
          if (mobileApp) {
            return true;
          }
          return false;
        }

        const value = deployment?.find((dep) => dep === generalPlatform.value);

        return !!value;
      })
      .map((platform) => platform.value);

    const ratingBW = system?.ratingBW;

    const companySizes = system?.clientCompanySize.map(
      (size) => CONSTANTS_SIZE[size.toUpperCase()]
    );

    const functions = [];
    generalFunctions.forEach((categoryFunctions) => {
      const items = categoryFunctions.items.map((func) => {
        if (!system?.systemProps) {
          return {
            key: func.key,
            value: 'NO',
          };
        }
        const value = system?.systemProps[categoryFunctions?.key]
          ? system?.systemProps[categoryFunctions?.key][func?.key]
          : null;

        return {
          key: func.key,
          value: value || 'NO',
        };
      });

      functions.push({
        key: categoryFunctions.key,
        items,
      });
    });

    let answers = [];
    if (isCustomer(customerType)) {
      answers = [
        {
          question_id: 1,
          text: unEscapeHtml(whySystemDescription),
        },
        {
          question_id: 2,
          text: unEscapeHtml(whyCompanyDescription),
        },
        {
          question_id: 3,
          text: unEscapeHtml(marketPositionDescription),
        },
        {
          question_id: 4,
          text: unEscapeHtml(solvesClientDescription),
        },
      ];
    }

    return {
      id: system.id,
      name,
      category: defaultCategory,
      company,
      logoUrl,
      ratingBW,
      companySizes,
      answers,
      functions,
      prices,
      platforms,
      averageRatings,
      reviewsCount: allSystemReviews.length,
      pros,
      cons,
      pitchVideos,
      readMoreHref,
      readAboutMoreHref,
      trackingFn,
    };
  });

  generalPrices.items = !hasPriceAdditional
    ? generalPrices.items.filter((item) => item.key !== 'priceAdditional')
    : generalPrices.items;

  generalPrices.items = !hasPrice
    ? generalPrices.items.filter((item) => item.key !== 'price')
    : generalPrices.items;

  const generalComponentData = {
    legalText: t('legal.compare.text'),
    link2Text: t('compare.backToCategory'),
    link2Href: '/#', // TODO: Link to category
    companySizesTitle: t('compare.companySize'),
    companySizes: generalCompanySizes,
    platforms: generalPlatforms,
    prices: generalPrices,

    questions: {
      title: t('compare.whyCompanyChooseSystem'),
      items: [
        {
          id: 1,
          text: t('compare.whyChooseSystem'),
          iconImg: <Hands width={40} height={40} />,
        },
        {
          id: 2,
          text: t('compare.whyChooseCompany'),
          iconImg: <Touch width={40} height={40} />,
        },
        {
          id: 3,
          text: t('compare.whyChooseMarketPosition'),
          iconImg: <Tasks width={40} height={40} />,
        },
        {
          id: 4,
          text: t('compare.whatNeedsChallengesSolvingCustomer'),
          iconImg: <Graph width={40} height={40} />,
        },
      ],
    },
    functions: generalFunctions,
    reviews: {
      title: t('sp.reviews'),
      subtitle: t('sp.overallRating'),
    },
  };

  const sections = [
    {
      sectionType: 'dubble',
      type: 'compareSystems',
      components: [
        {
          type: 'systems',
          componentData: systemsComponentData,
        },
        {
          type: 'general',
          componentData: generalComponentData,
        },
      ],
    },
    {
      sectionType: 'single',
      type: 'relatedCategories',
      component: {
        type: 'relatedCategories',
        title:
          comparedCategories?.length > 1
            ? t('common.relatedCategories')
            : undefined,
        componentData: comparedCategories.map((category) => ({
          name: category.categoryName,
          url: `/${category?.slugi18n}/`,
        })),
      },
    },
  ];

  return Compare({
    sections,
    t,
    locale,
    menu,
    footer,
    siteUrls,
    location,
    search,
    handleDataLayer,
  });
}

ComparsionTemplate.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
};

export default ComparsionTemplate;

export function Head({ location, pageContext, data }) {
  const { locale, noindex } = pageContext;
  const [, compareSystemPageData] = useStrapiPageData(
    PAGE_COMPARE_SYSTEM_STATIC
  );

  const { t } = useTranslation();
  const bwPageBuildData = useTransformGatsbyData(data.bwPageBuildData);
  const comparedCategories = useTransformGatsbyData(data.comparedCategories);
  const comparedSystems = useTransformGatsbyData(data.comparedSystems);

  const { seo: seoMetadata } = compareSystemPageData;
  const siteUrls = bwPageBuildData.pageData.siteUrls || {};

  const categories = categoryLinksCms(comparedCategories);
  const categoriesNames = categories.map((catData) => catData.name);
  const categoriesNiceNames = getArrayItemsToStringNice(categoriesNames, t);

  let { metaTitle } = seoMetadata;
  let { metaDescription } = seoMetadata;
  comparedSystems.forEach((system, index) => {
    const { systemName } = system.systemSiteDataForLocale;
    const systemIndex = index + 1;

    metaTitle = metaTitle.replaceAll(`{{system${systemIndex}}}`, systemName);

    metaDescription = metaDescription.replaceAll(
      `{{system${systemIndex}}}`,
      systemName
    );

    metaDescription = metaDescription.replaceAll(
      '{{categories}}',
      categoriesNiceNames
    );
  });

  const year = new Date().getFullYear();
  metaTitle = metaTitle.replaceAll(`{{year}}`, year);
  metaDescription = metaDescription.replaceAll(`{{year}}`, year);

  return (
    <MetatagsForLayout
      title={metaTitle}
      description={metaDescription}
      pathname={location.pathname}
      locale={locale}
      siteUrls={siteUrls}
      noindex={noindex}
    />
  );
}
